import { useMemo } from "react";
import { Plus, Trash2, AlertTriangle, Check } from "lucide-react";
import type { PricingPlanRecord } from "./types";

/* ── Types ────────────────────────────────────────────────────────── */

export interface TierDraft {
  /** Client-only key for React list stability */
  _key: string;
  pricingPlanId: string;
  minCustomerCount: number;
  maxCustomerCount: number;
}

interface TierEditorProps {
  tiers: TierDraft[];
  onChange: (tiers: TierDraft[]) => void;
  plans: PricingPlanRecord[];
  minCustomers: number;
  maxCustomers: number;
}

/* ── Validation helpers ───────────────────────────────────────────── */

export interface TierValidation {
  /** All ranges fully cover [min, max] with no gaps */
  fullCoverage: boolean;
  /** No ranges overlap */
  noOverlap: boolean;
  /** All tier ranges are within [min, max] bounds */
  withinBounds: boolean;
  /** No tier has its minimum above its maximum */
  noInvertedRanges: boolean;
  /** Primary status message (hard error or all-clear) */
  message: string;
  /** Non-blocking advisory messages (coverage gaps, out-of-bounds) */
  warnings: string[];
  /** Whether the tiers pass hard validation (no overlaps, no inverted ranges) */
  valid: boolean;
}

export function validateTiers(
  tiers: TierDraft[],
  min: number,
  max: number,
): TierValidation {
  if (tiers.length === 0) {
    return {
      fullCoverage: false,
      noOverlap: true,
      withinBounds: true,
      noInvertedRanges: true,
      message: "No tiers configured.",
      warnings: [],
      valid: false,
    };
  }

  const sorted = [...tiers].sort(
    (a, b) => a.minCustomerCount - b.minCustomerCount,
  );

  // Check for inverted ranges (min > max within a single tier)
  const noInvertedRanges = sorted.every(
    (t) => t.minCustomerCount <= t.maxCustomerCount,
  );

  // Check within bounds
  const withinBounds = sorted.every(
    (t) => t.minCustomerCount >= min && t.maxCustomerCount <= max,
  );

  // Check overlaps
  let noOverlap = true;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].minCustomerCount <= sorted[i - 1].maxCustomerCount) {
      noOverlap = false;
      break;
    }
  }

  // Check full coverage
  let cursor = min;
  let fullCoverage = true;
  for (const t of sorted) {
    if (t.minCustomerCount > cursor) {
      fullCoverage = false;
      break;
    }
    cursor = Math.max(cursor, t.maxCustomerCount + 1);
  }
  if (cursor <= max) fullCoverage = false;

  // Hard errors: overlaps or inverted ranges (block save, match backend checks)
  const valid = noOverlap && noInvertedRanges;

  // Build primary message (describes hard-error state or all-clear)
  let message: string;
  if (!noInvertedRanges) {
    message = "A tier has its minimum above its maximum.";
  } else if (!noOverlap) {
    message = "Tier ranges have overlapping customer counts.";
  } else {
    message = "Tier ranges are valid.";
  }

  // Soft warnings (advisory, don't block save)
  const warnings: string[] = [];
  if (noInvertedRanges && !withinBounds) {
    warnings.push(
      `Some tier ranges fall outside the service bounds of ${min}–${max}.`,
    );
  }
  if (noInvertedRanges && noOverlap && !fullCoverage) {
    warnings.push(
      `Tier ranges do not fully cover ${min}–${max}. Some customer counts have no pricing tier.`,
    );
  }

  // Upgrade message when everything is perfect
  if (valid && warnings.length === 0) {
    message = `Tier ranges fully cover ${min}–${max} with no gaps or overlaps.`;
  }

  return {
    fullCoverage,
    noOverlap,
    withinBounds,
    noInvertedRanges,
    message,
    warnings,
    valid,
  };
}

/* ── Component ────────────────────────────────────────────────────── */

let _keyCounter = 0;
export function makeTierKey(): string {
  return `tier-${Date.now()}-${++_keyCounter}`;
}

export function TierEditor({
  tiers,
  onChange,
  plans,
  minCustomers,
  maxCustomers,
}: TierEditorProps) {
  const validation = useMemo(
    () => validateTiers(tiers, minCustomers, maxCustomers),
    [tiers, minCustomers, maxCustomers],
  );

  const plansById = useMemo(() => {
    const m = new Map<string, PricingPlanRecord>();
    for (const p of plans) m.set(p.id, p);
    return m;
  }, [plans]);

  function addTier() {
    onChange([
      ...tiers,
      {
        _key: makeTierKey(),
        pricingPlanId: plans[0]?.id ?? "",
        minCustomerCount: minCustomers,
        maxCustomerCount: maxCustomers,
      },
    ]);
  }

  function removeTier(idx: number) {
    onChange(tiers.filter((_, i) => i !== idx));
  }

  function updateTier(idx: number, patch: Partial<TierDraft>) {
    onChange(tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[12px] font-semibold text-ink-2">
            Pricing tiers
          </div>
          <div className="text-[11px] text-ink-4">
            Each customer-count range bills under a different plan.
          </div>
        </div>
        <button
          type="button"
          onClick={addTier}
          className="text-[11.5px] text-tide hover:text-tide-d font-medium flex items-center gap-1"
        >
          <Plus size={11} strokeWidth={2.2} />
          Add tier
        </button>
      </div>

      {tiers.length > 0 && (
        <div className="bg-paper-hi rounded-md hair border overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[88px_1fr_90px_90px_40px] gap-3 px-4 py-2 hair-b bg-canvas/30 text-[10.5px] font-semibold text-ink-4 uppercase tracking-wider">
            <div>Range</div>
            <div>Plan</div>
            <div className="text-right">Auth</div>
            <div className="text-right">Hourly</div>
            <div />
          </div>

          <div className="divide-hair">
            {tiers.map((tier, idx) => {
              const plan = plansById.get(tier.pricingPlanId);
              return (
                <div
                  key={tier._key}
                  className="grid grid-cols-[88px_1fr_90px_90px_40px] gap-3 px-4 py-2.5 items-center"
                >
                  {/* Range inputs */}
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={tier.minCustomerCount}
                      onChange={(e) =>
                        updateTier(idx, {
                          minCustomerCount:
                            parseInt(e.target.value, 10) || 1,
                        })
                      }
                      className="w-11 px-1.5 py-1 rounded hair border bg-canvas/30 text-[12px] num text-center focus:outline-none focus:ring-2 focus:ring-tide/40"
                    />
                    <span className="text-ink-5 text-[11px]">–</span>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={tier.maxCustomerCount}
                      onChange={(e) =>
                        updateTier(idx, {
                          maxCustomerCount:
                            parseInt(e.target.value, 10) || 1,
                        })
                      }
                      className="w-11 px-1.5 py-1 rounded hair border bg-canvas/30 text-[12px] num text-center focus:outline-none focus:ring-2 focus:ring-tide/40"
                    />
                  </div>

                  {/* Plan select */}
                  <select
                    value={tier.pricingPlanId}
                    onChange={(e) =>
                      updateTier(idx, { pricingPlanId: e.target.value })
                    }
                    className="w-full px-2 py-1 rounded hair border bg-canvas/30 text-[12px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                  >
                    <option value="">Select plan...</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>

                  {/* Auth */}
                  <div className="text-[12.5px] num text-right text-ink-3">
                    {plan ? `$${plan.authAmount.toLocaleString()}` : "—"}
                  </div>

                  {/* Hourly */}
                  <div className="text-[12.5px] num text-right text-ink-3">
                    {plan ? `$${plan.hourlyRate.toLocaleString()}/hr` : "—"}
                  </div>

                  {/* Delete */}
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => removeTier(idx)}
                      className="text-ink-5 hover:text-rust p-1"
                    >
                      <Trash2 size={12} strokeWidth={2} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Validation messages */}
      {tiers.length > 0 && (
        <div className="mt-2.5 space-y-1">
          <div className="text-[11px] flex items-center gap-1.5">
            {!validation.valid ? (
              <>
                <AlertTriangle
                  size={11}
                  strokeWidth={3}
                  className="text-rust flex-shrink-0"
                />
                <span className="text-rust">{validation.message}</span>
              </>
            ) : validation.warnings.length > 0 ? (
              <>
                <AlertTriangle
                  size={11}
                  strokeWidth={3}
                  className="text-ink-5 flex-shrink-0"
                />
                <span className="text-ink-4">{validation.message}</span>
              </>
            ) : (
              <>
                <Check
                  size={11}
                  strokeWidth={3}
                  className="text-sage flex-shrink-0"
                />
                <span className="text-ink-4">{validation.message}</span>
              </>
            )}
          </div>
          {validation.warnings.map((w, i) => (
            <div
              key={i}
              className="text-[10.5px] text-ink-5 ml-[15.5px]"
            >
              {w}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
